import { Observable } from "rxjs";
import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router } from "@angular/router";
import { catchError, map, take } from "rxjs/operators";
import {
  InvestigationService,
  ContactService,
  AuthService,
  CompanyService,
  UserService,
} from "src/app/shared/services";
import {
  IApiCompany,
  IApiContact,
  IApiInvestigation,
  IApiInvestigationRoleNames,
  IApiUserFilterType,
} from "./modules/graphql/types/types";
import { removeTypename } from "./rxjs.pipes";
import { LoaderService } from "./modules/loader/loader.service";
import { Capacitor } from "@capacitor/core";
import { ListPage } from "./helpers/list-page.class";
import { unwrapConnection } from "./rxjs.pipes";
import { StateService } from "./services/state.service";
const isMobile = Capacitor.isNativePlatform();

@Injectable()
export class PermissionsResolver {
  constructor(private auth: AuthService) {}

  resolve(_route: ActivatedRouteSnapshot) {
    return this.auth.assignedCategories.pipe(take(1));
  }
}

@Injectable()
export class InvestigationResolver {
  constructor(
    private investigationService: InvestigationService,
    private loader: LoaderService,
    private router: Router
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    return this.loader
      .show$(
        isMobile
          ? this.investigationService.getInvestigationByIdMobile(
              route.params.investigationId,
              true
            )
          : this.investigationService.getById(route.params.investigationId)
      )
      .pipe(
        removeTypename(),
        catchError((err: any) => {
          if (err?.message === "Permission denied.") {
            this.router.navigate(["admin/access-restricted"]);
          } else if (err?.message?.includes("Access Restricted For User.")) {
            const nefcoNumber = err?.message?.split("NEFCO_NUMBER:");
            this.router.navigate(["admin/access-restricted"], {
              queryParams: {
                nefcoNumber: nefcoNumber?.length
                  ? nefcoNumber[1]
                  : "Unassigned",
              },
            });
          } else {
            return err;
          }
        })
      );
  }
}

@Injectable()
export class ContactResolver {
  constructor(private contactService: ContactService) {}

  resolve(route: ActivatedRouteSnapshot) {
    return this.contactService
      .getById(route.params.contactId)
      .pipe(removeTypename());
  }
}

@Injectable()
export class CalendarUsersResolver {
  constructor(
    private userService: UserService,
    private stateService: StateService
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    const filters = [
      {
        type: IApiUserFilterType.AddressType,
        value: "Home",
      },
      {
        type: IApiUserFilterType.Position,
        value: JSON.stringify([
          IApiInvestigationRoleNames.Investigator.valueOf(),
          IApiInvestigationRoleNames.RegionalManager.valueOf(),
          IApiInvestigationRoleNames.AutoExpert.valueOf(),
          IApiInvestigationRoleNames.EngineerTechReviewer.valueOf(),
          IApiInvestigationRoleNames.HvacExpert.valueOf(),
          IApiInvestigationRoleNames.EvidenceTechnician.valueOf(),
          IApiInvestigationRoleNames.TechReviewer.valueOf(),
          IApiInvestigationRoleNames.FireProtectionEngineer.valueOf(),
          IApiInvestigationRoleNames.ElectricalEngineer.valueOf(),
        ]),
      },
      {
        type: IApiUserFilterType.ViewCalendarView,
        value: "true",
      },
    ];

    return this.userService.get(filters, new ListPage(-1)).pipe(
      unwrapConnection(),
      map((results: any[]) => {
        const allStates = this.stateService.allStates;

        return results.map((u) => {
          const stateAbbr = u.HomeAddress?.state;
          const stateObj = allStates.find((s: any) => s.value === stateAbbr);

          return {
            ...u,
            itemFullName: `${u.firstName} ${u.lastName}`,
            stateName: stateObj ? stateObj.text : stateAbbr || "Unknown",
          };
        });
      })
    );
  }
}
